import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  AppRepository,
  LocalRepository,
  HttpRepository,
  CURRENT_USER,
  type Snapshot,
  type Command,
  type ThemeName,
  themes,
  type RecordStorage,
  applyCommand,
  type AITask,
  setCurrentUser,
} from '@ventureroom/core';
import { ZodError } from 'zod';
import { Platform } from 'react-native';
export interface Preferences {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
}
export type Destination =
  | 'discover'
  | 'connections'
  | 'ventures'
  | 'activity'
  | 'profile'
  | 'settings'
  | 'dashboard'
  | 'tools'
  | `person/${string}`
  | `venture/${string}`
  | `chat/${string}`
  | `meeting/${string}`
  | `boardroom/${string}`;
type State = {
  authenticated: boolean;
  allowDemo: boolean;
  authenticate: (
    username: string,
    password: string,
    signup: boolean,
    collaborationBrief?: string,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  data: Snapshot | null;
  theme: ThemeName;
  ready: boolean;
  entered: boolean;
  busy: boolean;
  notice: string;
  error: string;
  mode: 'local' | 'server';
  navigate: (to: Destination) => void;
  goBack: (fallback: Destination) => void;
  act: (c: Command, success?: string) => Promise<boolean>;
  refresh: () => Promise<void>;
  setTheme: (theme: ThemeName) => Promise<void>;
  enter: () => Promise<void>;
  startProfile: (input: unknown) => Promise<boolean>;
  reset: () => Promise<void>;
  pair: (url: string, token: string) => Promise<boolean>;
  disconnect: () => Promise<void>;
  announce: (s: string) => void;
  reduceMotion: boolean;
  setReduceMotion: (b: boolean) => Promise<void>;
  tasks: AITask[];
  cancelTask: (id: string) => Promise<void>;
  artifact: (id: string) => Promise<Uint8Array>;
  haptic: () => void;
};
const Context = createContext<State | null>(null);
export function AppProvider({
  children,
  storage,
  preferences,
  navigate,
  goBack,
  haptic = () => undefined,
  allowDemo = false,
}: {
  children: ReactNode;
  storage: RecordStorage;
  preferences: Preferences;
  navigate: State['navigate'];
  goBack: State['goBack'];
  haptic?: () => void;
  allowDemo?: boolean;
}) {
  const local = useRef(new LocalRepository(storage));
  const repo = useRef<AppRepository>(local.current);
  const authURL =
    typeof window !== 'undefined' && Platform.OS === 'web'
      ? window.location.origin
      : 'https://ventureroom.procuraq.com';
  const sessionToken = useRef('');
  const [authenticated, setAuthenticated] = useState(false);
  const [data, setData] = useState<Snapshot | null>(null),
    [theme, changeTheme] = useState<ThemeName>('warm'),
    [ready, setReady] = useState(false),
    [entered, setEntered] = useState(false),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(''),
    [error, setError] = useState(''),
    [mode, setMode] = useState<'local' | 'server'>('local'),
    [reduceMotion, changeMotion] = useState(false),
    [tasks, setTasks] = useState<AITask[]>([]);
  const format = (e: unknown) =>
    e instanceof ZodError
      ? `${e.issues[0]?.path.join(' ')}: ${e.issues[0]?.message}`
      : e instanceof Error
        ? e.message
        : 'Something went wrong. Please try again.';
  const refresh = useCallback(async () => {
    const current = repo.current;
    try {
      const next = await current.read();
      if (current !== repo.current) return;
      setData(next);
      setError('');
    } catch (e) {
      if (current !== repo.current) return;
      if (e instanceof Error && e.message === 'Please sign in to VentureRoom.') {
        setAuthenticated(false);
        setData(null);
        setTasks([]);
      }
      setError(format(e));
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [savedTheme, entry, motion, url, token] = await Promise.all(
          ['theme', 'entered', 'motion', 'backend-url', 'backend-token'].map((k) =>
            preferences.get(k),
          ),
        );
        if (!mounted) return;
        changeTheme(savedTheme === 'dark' ? 'dark' : 'warm');
        setEntered(entry === 'yes');
        changeMotion(motion === 'reduced');
        // Remove legacy browser-side provider credentials; keys now remain server-side.
        await preferences.set('openai-key', '');
        const savedSession =
          Platform.OS === 'web' ? '' : ((await preferences.get('auth-token')) ?? '');
        if (!allowDemo) {
          try {
            const http = new HttpRepository(authURL, savedSession);
            setCurrentUser((await http.identity()).userId);
            sessionToken.current = savedSession;
            repo.current = http;
            setMode('server');
            setAuthenticated(true);
            setEntered(true);
          } catch {
            setAuthenticated(false);
          }
        }
        if (allowDemo && url && token && repo.current === local.current) {
          const http = new HttpRepository(url, token);
          setCurrentUser((await http.identity()).userId);
          repo.current = http;
          setMode('server');
        }
        await refresh();
      } catch (e) {
        setError(format(e));
        setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [preferences, refresh, authURL, allowDemo]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 4500);
    return () => clearTimeout(timer);
  }, [notice]);
  async function act(c: Command, success = 'Saved to your workspace') {
    const current = repo.current;
    setBusy(true);
    setError('');

    // Optimistic update so the UI responds instantly!
    if (data) {
      try {
        setData(applyCommand(data, c, CURRENT_USER));
      } catch (e) {
        setError(format(e));
        setBusy(false);
        return false;
      }
    }

    try {
      const next = await current.execute(c);
      if (current !== repo.current) return false;
      setData(next);
      setNotice(success);
      if (!reduceMotion) haptic();
      return true;
    } catch (e) {
      if (current !== repo.current) return false;
      const next = await current.read();
      if (current !== repo.current) return false;
      setData(next);
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function setTheme(t: ThemeName) {
    await preferences.set('theme', t);
    changeTheme(t);
  }
  async function enter() {
    await preferences.set('entered', 'yes');
    setEntered(true);
  }
  async function startProfile(input: unknown) {
    if (entered || mode !== 'local') return false;
    setBusy(true);
    try {
      setData(await local.current.startProfile(input));
      await enter();
      setNotice('Your founder profile is ready');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function reset() {
    try {
      const next = await repo.current.reset();
      setData(next);
      setNotice('Demo data restored');
      navigate('discover');
    } catch (e) {
      setError(format(e));
    }
  }
  async function pair(url: string, token: string) {
    setBusy(true);
    try {
      const next = new HttpRepository(url.trim(), token.trim());
      const identity = await next.identity();
      const state = await next.read();
      setCurrentUser(identity.userId);
      await preferences.set('backend-url', url.trim());
      await preferences.set('backend-token', token.trim());
      repo.current = next;
      setData(state);
      setMode('server');
      setError('');
      setNotice('Connected to your local backend');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function disconnect() {
    if (authenticated) {
      await logout();
      return;
    }
    await preferences.set('backend-token', '');
    await preferences.set('backend-url', '');
    repo.current = local.current;
    setTasks([]);
    setMode('local');
    await refresh();
    setNotice('Your offline workspace is ready');
  }
  async function authenticate(
    username: string,
    password: string,
    signup: boolean,
    collaborationBrief = '',
  ) {
    setBusy(true);
    setError('');
    try {
      const response = await fetch(authURL + '/api/auth/' + (signup ? 'register' : 'login'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(Platform.OS !== 'web' ? { 'X-VentureRoom-Native': 'true' } : {}),
        },
        body: JSON.stringify({ username, password, ...(signup ? { collaborationBrief } : {}) }),
        signal: AbortSignal.timeout(15000),
      });
      const result = await response.json();
      if (!response.ok) throw Error(result.error ?? 'Could not sign in.');
      sessionToken.current = result.token ?? '';
      if (Platform.OS !== 'web') await preferences.set('auth-token', sessionToken.current);
      setCurrentUser(result.userId);
      repo.current = new HttpRepository(authURL, sessionToken.current);
      setData(await repo.current.read());
      setTasks([]);
      setMode('server');
      setAuthenticated(true);
      setEntered(true);
      navigate('discover');
      return true;
    } catch (e) {
      setError(format(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function logout() {
    try {
      const response = await fetch(authURL + '/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + sessionToken.current,
        },
        body: '{}',
        signal: AbortSignal.timeout(12000),
      });
      if (!response.ok) throw Error('Could not sign out. Try again.');
      await preferences.set('auth-token', '');
      await preferences.set('backend-token', '');
      await preferences.set('backend-url', '');
      sessionToken.current = '';
      setAuthenticated(false);
      setEntered(false);
      setTasks([]);
      setData(null);
      setMode('local');
      repo.current = local.current;
      setCurrentUser('jamie');
      setNotice('');
      setError('');
      navigate('discover');
    } catch (e) {
      setError(format(e));
    }
  }
  async function cancelTask(id: string) {
    if (repo.current instanceof HttpRepository) {
      await repo.current.cancelTask(id);
      setTasks(await repo.current.tasks());
    }
  }
  async function artifact(id: string) {
    if (!(repo.current instanceof HttpRepository))
      throw Error('Connect to the backend to download this artifact.');
    return repo.current.artifact(id);
  }
  useEffect(() => {
    if (mode !== 'server') return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const current = repo.current;
        if (current instanceof HttpRepository) {
          const latest = await current.tasks();
          if (!disposed && current === repo.current) {
            setTasks(latest);
            await refresh();
          }
        }
      } catch {
        if (!disposed) await refresh();
      }
      if (!disposed) timer = setTimeout(() => void poll(), 5000);
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [mode, refresh]);
  async function setReduceMotion(b: boolean) {
    await preferences.set('motion', b ? 'reduced' : 'normal');
    changeMotion(b);
  }
  return (
    <Context.Provider
      value={{
        authenticated,
        allowDemo,
        authenticate,
        logout,
        data,
        theme,
        ready,
        entered,
        busy,
        notice,
        error,
        mode,
        navigate,
        goBack,
        act,
        refresh,
        setTheme,
        enter,
        startProfile,
        reset,
        pair,
        disconnect,
        announce: setNotice,
        reduceMotion,
        setReduceMotion,
        tasks,
        cancelTask,
        artifact,
        haptic,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useApp() {
  const value = useContext(Context);
  if (!value) throw Error('AppProvider is required');
  return value;
}
export function useTheme() {
  return themes[useApp().theme];
}
export function useData() {
  const { data } = useApp();
  if (!data) throw Error('Workspace is not ready');
  return data;
}
export function useMe() {
  return useData().profiles.find((p) => p.id === CURRENT_USER)!;
}
